import { useEffect, useMemo } from 'react';
import { Container } from 'theme-ui';
import { useSettings } from '@backpackjs/storefront';

import {
  useDataLayerActions,
  usePickedSections,
  useGatedProduct,
} from '@hooks';
import store, { useRecoilValue, useSetRecoilState } from '@store';

import { Breadcrumbs } from '@snippets';
import { CollectionGrid } from './CollectionGrid';
import { SeoFooter } from './SeoFooter';
import { MobileFilters } from './Filters';
import { useCollection, useCollectionFilters } from './hooks';

function CollectionComponent({ collection, sections }) {
  const settings = useSettings();
  const gatedPage = useRecoilValue(store.gatedPage);
  const { sendViewCollectionEvent } = useDataLayerActions();

  const { gatedProducts } = useGatedProduct({
    gatedPage,
    currentUrl: collection.handle,
  });

  const { firstOf, otherSections } = usePickedSections({
    firstOf: ['collection-hero', 'collection-categories'],
    sections,
  });
  const { products, merchandising } = useCollection();
  const [, { clearSelectedFilters }] = useCollectionFilters();

  const setSwatchesMap = useSetRecoilState(store.swatchesMap);

  const swatches = settings?.collection?.colors?.swatches || [];

  const collectionHero = useMemo(() => {
    if (firstOf['collection-hero']) {
      if (
        firstOf['collection-hero']?.props?.children?.props?.children?.props?.cms
      ) {
        firstOf[
          'collection-hero'
        ].props.children.props.children.props.cms.showBreadCrumb = true;
      }
      return firstOf['collection-hero'];
    }
    const haflMedia = otherSections?.find((s, i) => {
      if (s?.props?.children?.props?.children?.props?.cms) {
        s.props.children.props.children.props.cms.showBreadCrumb = true;
      }
      return s?.props?.['data-comp'] === 'half-media' && i === 0;
    });
    if (haflMedia) {
      return haflMedia;
    }
    return null;
  }, [firstOf['collection-hero'], otherSections]);

  useEffect(() => {
    const _swatchesMap = swatches.reduce((map, swatch) => {
      map[swatch.name?.toLowerCase()] = swatch;
      return map;
    }, {});
    setSwatchesMap(_swatchesMap);
  }, [JSON.stringify(swatches)]);

  useEffect(() => {
    return () => {
      clearSelectedFilters();
    };
  }, [collection?.handle]);

  useEffect(() => {
    sendViewCollectionEvent({
      products: collection?.products,
      collectionId: collection?.id,
    });
  }, [
    `${collection?.id}${!!collection?.products?.length}`,
    sendViewCollectionEvent,
  ]);

  return (
    <Container data-comp="CollectionTemplate">
      {firstOf['collection-hero'] || null}

      {!collectionHero && (
        <Breadcrumbs isCollection collectionTitle={collection?.title} />
      )}

      {otherSections}

      {!gatedProducts && (
        <MobileFilters
          collection={collection}
          hasHero={!!firstOf['collection-hero']}
          collectionCategories={firstOf['collection-categories'] || null}
        />
      )}

      {/* Product items grid */}
      <Container
        sx={{
          variant: 'layout.content',
          display: 'block',
          pt: [0, 14, 15],
          pb: [14, 18, '80px'],
        }}
      >
        <CollectionGrid
          products={gatedProducts || products}
          merchandising={merchandising}
          hasInitialProducts={!!collection.products?.length}
          gatedPage={gatedPage}
        />

        <SeoFooter />
      </Container>
    </Container>
  );
}

export function CollectionTemplate({ collection, renderSections }) {
  const sections = renderSections();
  return <CollectionComponent sections={sections} collection={collection} />;
}
